import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, ChevronDown, ChevronRight, X, LayoutGrid, Store, SearchX, ShieldAlert } from "lucide-react";
import { LangTextToggle, EmptyState, Spinner } from "../components/common";
import ListingCard, { ListingSkeleton } from "../components/ListingCard";
import FilterSheet, { SORTS } from "../components/FilterSheet";
import { ZoneDialog } from "../components/Dialogs";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { useAuth } from "../context/AuthContext";
import { useRegion } from "../context/RegionContext";

const STORE_KEY = "mm_filters";
const blank = (zone) => ({ q: "", category: "", looking_for: "", zone: zone || "all", price_min: "", price_max: "", sort: "newest" });
const LOOKING = ["", "amistad", "relacion", "rollos", "sexo"];
const catPatch = (c) => ({ category: c, looking_for: c === "contactos" ? "amistad" : "" });

function ContactTabs({ value, onChange }) {
  const { t } = useLang();
  const { optLabel } = useAppData();
  return (
    <div className="px-4 pb-2.5" data-no-swipe data-testid="contactos-tabs">
      <div className="flex w-full gap-0.5 rounded-full bg-mist p-1 ring-1 ring-slate-200">
        {LOOKING.map((k) => (
          <button key={k || "all"} onClick={() => onChange(k)} data-testid={`contactos-tab-${k || "all"}`}
            className={`rounded-full py-2.5 font-bold transition-colors duration-200 ${k ? "min-w-0 flex-1 truncate px-0.5 text-[12.5px] min-[360px]:px-1 min-[360px]:text-[14px] sm:text-[15px]" : "shrink-0 px-2 text-[11px] min-[360px]:px-2.5"} ${
              value === k ? "bg-brand text-white shadow-[0_4px_12px_rgba(30,136,229,0.35)]" : "text-navy hover:bg-white"}`}>
            {k ? optLabel("looking_for", k) : t("contact.all")}
          </button>
        ))}
      </div>
    </div>
  );
}

function toParams(f, page) {
  const p = { page, sort: f.sort };
  if (f.q.trim()) p.q = f.q.trim();
  if (f.category) p.category = f.category;
  if (f.category === "contactos" && f.looking_for) p.looking_for = f.looking_for;
  if (f.zone && f.zone !== "all") p.zone = f.zone;
  if (f.price_min !== "") p.price_min = f.price_min;
  if (f.price_max !== "") p.price_max = f.price_max;
  return p;
}

function CategoryCircle({ active, onClick, testid, circleRef, label, children, badge }) {
  return (
    <button ref={circleRef} onClick={onClick} data-testid={testid} aria-pressed={active}
      className="flex w-[68px] shrink-0 flex-col items-center gap-1.5 transition-transform duration-200 active:scale-95">
      <span className={`relative flex h-[60px] w-[60px] items-center justify-center rounded-full p-[3px] transition-[box-shadow] duration-200 ${
        active ? "ring-[3px] ring-[#0D47A1]" : "ring-[1.5px] ring-slate-200"}`}>
        <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-mist">{children}</span>
        {badge && <span className="absolute -right-1 -top-0.5 rounded-full bg-alert px-1 py-px text-[9px] font-extrabold leading-tight text-white ring-2 ring-white">{badge}</span>}
      </span>
      <span className={`line-clamp-2 text-center text-[12px] leading-tight ${active ? "font-extrabold text-[#0D47A1]" : "font-medium text-navy"}`}>{label}</span>
    </button>
  );
}

function CategoryRow({ value, onChange, onSeeAll }) {
  const { lang } = useLang();
  const { categories } = useAppData();
  const rowRef = useRef(null);
  const refs = useRef({});
  useEffect(() => {
    const row = rowRef.current;
    const el = refs.current[value || "all"];
    if (row && el) row.scrollTo({ left: el.offsetLeft - row.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [value, categories.length]);
  return (
    <div ref={rowRef} className="no-scrollbar flex gap-1.5 overflow-x-auto px-3 pb-2.5 pt-1.5 touch-pan-x" data-no-swipe data-testid="category-chip-row">
      <CategoryCircle active={!value} onClick={() => onChange("")} testid="category-chip-all" circleRef={(el) => (refs.current.all = el)} label={lang === "en" ? "All" : "Todos"}>
        <LayoutGrid className={`h-6 w-6 ${!value ? "text-[#0D47A1]" : "text-navy/70"}`} />
      </CategoryCircle>
      {categories.map((c) => (
        <CategoryCircle key={c.key} active={value === c.key} onClick={() => onChange(c.key)} testid={`category-chip-${c.key}`}
          circleRef={(el) => (refs.current[c.key] = el)} label={lang === "en" ? c.name_en : c.name_es} badge={c.adult ? "+18" : null}>
          <img src={c.image} alt="" className="h-full w-full object-cover" loading="lazy" />
        </CategoryCircle>
      ))}
      <CategoryCircle active={false} onClick={onSeeAll} testid="category-see-all-btn" label={lang === "en" ? "see all" : "ver todas"}>
        <ChevronRight className="h-6 w-6 text-brand" />
      </CategoryCircle>
    </div>
  );
}

function AllCategoriesSheet({ open, onOpenChange, value, onPick }) {
  const { lang } = useLang();
  const { categories } = useAppData();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[100dvh] overflow-y-auto border-0 px-4 pb-safe-6 pt-safe-6" data-no-swipe data-testid="all-categories-sheet">
        <SheetTitle className="text-2xl font-extrabold text-navy">{lang === "en" ? "All categories" : "Todas las categorías"}</SheetTitle>
        <SheetDescription className="sr-only">categories</SheetDescription>
        <div className="mx-auto mt-4 grid max-w-4xl grid-cols-3 gap-2.5 sm:grid-cols-5">
          <button onClick={() => onPick("")} data-testid="all-categories-option-all"
            className={`flex aspect-[5/4.6] flex-col items-center justify-center gap-2 rounded-2xl border-[1.5px] text-sm font-bold ${!value ? "border-brand bg-brand text-white" : "border-navy text-navy"}`}>
            <LayoutGrid className="h-7 w-7" /> {lang === "en" ? "All" : "Todos"}
          </button>
          {categories.map((c) => (
            <button key={c.key} onClick={() => onPick(c.key)} data-testid={`all-categories-option-${c.key}`}
              className={`flex flex-col rounded-2xl border-[1.5px] p-1.5 ${value === c.key ? "border-brand bg-brand text-white" : "border-navy bg-white text-navy"}`}>
              <img src={c.image} alt="" className="aspect-[5/4] w-full rounded-xl object-cover" loading="lazy" />
              <span className="flex min-h-[2rem] items-center justify-center text-center text-[12px] font-bold leading-tight">
                {lang === "en" ? c.name_en : c.name_es}
              </span>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function MercatScreen() {
  const { t, lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { region } = useRegion();
  const { zone, zoneName, catName, requireLogin, guardCategory } = useAppData();
  const [filters, setFilters] = useState(() => {
    const s = sessionStorage.getItem(STORE_KEY);
    return s ? JSON.parse(s) : blank(zone);
  });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [allCats, setAllCats] = useState(false);
  const [zoneOpen, setZoneOpen] = useState(false);
  const reqId = useRef(0);
  const sentinel = useRef(null);
  const prevZone = useRef(zone);

  useEffect(() => {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    if (prevZone.current !== zone) setFilters((f) => ({ ...f, zone }));
    prevZone.current = zone;
  }, [zone]);

  useEffect(() => {
    if (location.pathname !== "/mercat" || !location.search) return;
    const p = new URLSearchParams(location.search);
    setFilters((f) => ({ ...f, q: p.get("q") || "", ...catPatch(p.get("category") || "") }));
    navigate("/mercat", { replace: true });
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPage = useCallback(async (p, f) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const { data } = await api.get("/listings", { params: toParams(f, p) });
      if (id !== reqId.current) return;
      setItems((prev) => (p === 1 ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more);
      setTotal(data.total);
      setPage(p);
      setError(null);
    } catch (e) {
      if (id !== reqId.current) return;
      setError(e.response?.status || 500);
      if (p === 1) setItems([]);
      setHasMore(false);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  const fKey = JSON.stringify(filters);
  useEffect(() => {
    if (user === null) return;
    fetchPage(1, filters);
  }, [fKey, user === null, user?.user_id, user?.can_access_adult]); // eslint-disable-line react-hooks/exhaustive-deps

  const state = useRef({});
  state.current = { hasMore, loading, page, filters };
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const s = state.current;
        if (entry.isIntersecting && s.hasMore && !s.loading) fetchPage(s.page + 1, s.filters);
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [fetchPage]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const hasPrice = filters.price_min !== "" || filters.price_max !== "";
  const filtered = filters.q || hasPrice;
  const activeFilters = (filters.zone && filters.zone !== "all" ? 1 : 0) + (hasPrice ? 1 : 0);
  const [q, setQ] = useState(filters.q);
  const headerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setQ(filters.q);
  }, [filters.q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (q === filters.q) return undefined;
    const id = setTimeout(() => set({ q }), 450);
    return () => clearTimeout(id);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pane = headerRef.current?.closest("section");
    if (!pane) return undefined;
    const onScroll = () => setScrolled(pane.scrollTop > 4);
    pane.addEventListener("scroll", onScroll, { passive: true });
    return () => pane.removeEventListener("scroll", onScroll);
  }, []);

  const zoneLabel = filters.zone === "all" ? t("zone.allRegion") : zoneName(filters.zone);
  const showSkeleton = loading && items.length === 0 && error !== 403;
  const isAdult = filters.category === "contactos";
  const title = filters.category ? catName(filters.category) : t("mercat.allListings");

  return (
    <div className="min-h-full bg-white pb-16" data-testid="mercat-screen">
      <div ref={headerRef} className={`sticky top-0 z-20 bg-white/95 backdrop-blur-md transition-shadow duration-200 ${scrolled ? "shadow-[0_6px_20px_rgba(13,42,107,0.10)]" : ""}`} data-testid="mercat-header">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-[40px_minmax(0,1fr)_60px] items-center gap-2 px-3 pb-2 pt-safe-3 [--side:132px] min-[360px]:grid-cols-[84px_minmax(0,1fr)_60px] min-[360px]:[--side:176px]">
            <button onClick={() => setZoneOpen(true)} data-testid="mercat-zone-chip" aria-label={zoneLabel}
              className="flex min-w-0 max-w-full items-center gap-1 justify-self-start rounded-full bg-mist px-2 py-1.5 text-[11px] font-bold text-navy transition-colors hover:bg-slate-100">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-alert" />
              <span className="truncate max-[359px]:hidden">{filters.zone === "all" ? region.name : zoneLabel}</span>
            </button>
            <h1 key={title} className="hero-title mx-auto max-w-full truncate border-b-2 border-[#0D47A1] pb-0.5 leading-tight fade-up"
              style={{ fontSize: `max(15px, min(30px, 7.6vw, calc((100vw - var(--side)) / ${(title.length * 0.62).toFixed(2)})))` }}
              data-testid="mercat-title">
              {title}
            </h1>
            <LangTextToggle className="justify-self-end" />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); set({ q }); e.target.querySelector("input")?.blur(); }}
            className="mx-3 mb-1 flex items-center gap-2 rounded-full border border-slate-200 bg-mist/60 py-1 pl-4 pr-1 transition-colors focus-within:border-brand focus-within:bg-white" data-testid="mercat-search-form">
            <Search className="h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search.placeholder")} enterKeyHint="search"
              className="min-w-0 flex-1 bg-transparent py-2 text-[15px] text-navy outline-none placeholder:text-slate-400" data-testid="mercat-search-input" />
            {q && (
              <button type="button" onClick={() => { setQ(""); set({ q: "" }); }} className="rounded-full p-1.5 text-slate-400 hover:text-navy" aria-label="clear" data-testid="mercat-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
            <button type="button" onClick={() => setSheet("filters")} aria-label="filters" data-testid="mercat-filters-btn"
              className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-navy shadow-sm ring-1 ring-slate-200 transition-colors hover:text-brand">
              <SlidersHorizontal className="h-[18px] w-[18px]" />
              {activeFilters > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white" data-testid="mercat-filters-badge">{activeFilters}</span>
              )}
            </button>
          </form>
          <CategoryRow value={filters.category} onChange={(c) => guardCategory(c, () => set(catPatch(c)))} onSeeAll={() => setAllCats(true)} />
          {isAdult && <ContactTabs value={filters.looking_for || ""} onChange={(v) => set({ looking_for: v })} />}
        </div>
      </div>

      <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 pb-2.5 pt-3" data-testid="mercat-results-line">
        <p className="min-w-0 truncate text-[13px] text-slate-500" data-testid="mercat-subtitle">
          {!loading || items.length ? (
            <><span className="font-bold text-navy">{t(total === 1 ? "mercat.resultIn1" : "mercat.resultsIn", { n: total, zone: "" }).trim()}</span> {zoneLabel}</>
          ) : t("common.loading")}
        </p>
        {!isAdult && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[13px] font-bold text-[#0D47A1] hover:bg-mist" data-testid="sort-filter-chip">
                {t(`sort.${filters.sort}`)} <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              {SORTS.map((s) => (
                <DropdownMenuItem key={s} onSelect={() => set({ sort: s })} className={filters.sort === s ? "font-bold text-brand" : "text-navy"} data-testid={`sort-option-${s}`}>
                  {t(`sort.${s}`)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <main className="mx-auto max-w-6xl px-3 sm:px-4">
        {error === 403 ? (
          <EmptyState icon={ShieldAlert} title={t("gate.title")} sub={!user ? t("gate.loginSub") : t("gate.sub")} testid="mercat-adult-only"
            action={<button className="btn-primary" onClick={() => guardCategory("contactos", () => fetchPage(1, filters))} data-testid="mercat-verify-age-btn">{t("gate.verify")}</button>} />
        ) : showSkeleton ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 lg:grid-cols-4" data-testid="listings-skeleton">
            {Array.from({ length: 6 }, (_, i) => <ListingSkeleton key={i} portrait={isAdult} />)}
          </div>
        ) : !loading && items.length === 0 ? (
          filtered ? (
            <EmptyState icon={SearchX} title={t("empty.noResults")} sub={t("empty.noResultsSub")} testid="mercat-empty-filtered"
              action={<button className="btn-outline" onClick={() => setFilters(blank("all"))} data-testid="mercat-clear-filters-btn">{t("filters.clear")}</button>} />
          ) : (
            <EmptyState icon={Store} title={t("empty.firstIn", { zone: zoneName(filters.zone) })} sub={t("empty.firstInSub")} testid="mercat-empty"
              action={<button className="btn-primary" onClick={() => requireLogin() && navigate("/publicar")} data-testid="mercat-empty-publish-btn">{t("empty.publish")}</button>} />
          )
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 lg:grid-cols-4" data-testid="listings-grid">
            {items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
        <div ref={sentinel} className="h-4" data-testid="infinite-scroll-sentinel" />
        {loading && items.length > 0 && <div className="flex justify-center py-6"><Spinner /></div>}
        {!loading && hasMore && (
          <div className="flex justify-center py-4">
            <button className="btn-outline" onClick={() => fetchPage(page + 1, filters)} data-testid="load-more-btn">{t("mercat.loadMore")}</button>
          </div>
        )}
        {!loading && !hasMore && items.length > 0 && <p className="py-6 text-center text-xs text-slate-400" data-testid="listings-end">{t("mercat.end")}</p>}
      </main>

      <FilterSheet
        open={!!sheet}
        onOpenChange={(o) => !o && setSheet(null)}
        filters={filters}
        autoFocusKeyword={sheet === "search"}
        onApply={(f) => { setFilters(f); setSheet(null); }}
      />
      <AllCategoriesSheet open={allCats} onOpenChange={setAllCats} value={filters.category}
        onPick={(c) => { setAllCats(false); guardCategory(c, () => set(catPatch(c))); }} />
      <ZoneDialog open={zoneOpen} onOpenChange={setZoneOpen} />
      <span className="sr-only">{lang}</span>
    </div>
  );
}
